import { scenarioLibrary } from '../data/demoScenario.js';
import { normalizeScenario, scenarioFromParsed, validateScenarioForSave } from './scenario.js';
import { createRehearsalRecord, normalizeRehearsalRecord } from './rehearsalHistory.js';
import { createAppBackup, inspectAppBackup, mergeById, QLINE_BACKUP_POLICY } from './appBackup.js';
import { downloadDataBackup } from '../modules/import-export-kit/index.js';

const KEY = 'rehearsal-director:scenarios-v1';
const HISTORY_KEY = 'rehearsal-director:history-v1';
const SETTINGS_KEY = 'rehearsal-director:accessibility';
const FAVORITES_KEY = 'rehearsal-director:favorites';
const RECENT_KEY = 'rehearsal-director:recent-scenarios';
const PRACTICE_PREFIX = 'rehearsal-director:line-mastery-v2:';

export function loadScenarios() {
  try {
    const builtins = scenarioLibrary.map(normalizeScenario);
    const saved = new Map(JSON.parse(localStorage.getItem(KEY) || '[]').map(normalizeScenario).reverse().map(s => [s.id, s]));
    const known = new Set(builtins.map(s => s.id));
    return [...builtins.map(s => saved.get(s.id) || s), ...[...saved.values()].filter(s => !known.has(s.id)).reverse()];
  }
  catch { return scenarioLibrary.map(normalizeScenario); }
}

export function saveImportedScenario(parsed, title = 'Imported rehearsal', existingId, expectedSavedVersion) {
  const scenario = scenarioFromParsed(parsed, title, existingId);
  const current = loadUserScenarios({ strict: true });
  if (existingId && JSON.stringify(current.find(item => item.id === existingId)) !== expectedSavedVersion) throw Object.assign(new Error('This script changed in your library. Open the library to use that saved version, or save this import as a new script.'), { code: 'IMPORT_CONFLICT' });
  localStorage.setItem(KEY, JSON.stringify([scenario, ...current.filter(item => item.id !== scenario.id)]));
  return scenario;
}

export function loadUserScenarios({ strict = false } = {}) {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || '[]');
    const scenarios = saved.map(normalizeScenario);
    return scenarios;
  } catch {
    if (strict) throw new Error('Saved scenarios could not be read. They have not been overwritten. Restore a known backup before saving new changes.');
    return [];
  }
}

export function duplicateScenario(source) {
  const copy = normalizeScenario({ ...structuredClone(source), id: `copy-${Date.now()}`, title: `${source.title} — Copy`, importedAt: new Date().toISOString() });
  localStorage.setItem(KEY, JSON.stringify([copy, ...loadUserScenarios({ strict: true })]));
  return copy;
}

export function deleteScenario(id) {
  localStorage.setItem(KEY, JSON.stringify(loadUserScenarios({ strict: true }).filter((item) => item.id !== id)));
}

export function isUserScenario(id) { return loadUserScenarios().some((item) => item.id === id); }

export function saveScenario(input) {
  validateScenarioForSave(input);
  const scenario = normalizeScenario(input);
  const current = loadUserScenarios({ strict: true }).filter((item) => item.id !== scenario.id);
  localStorage.setItem(KEY, JSON.stringify([scenario, ...current]));
  return scenario;
}

export function markScenarioUsed(id) {
  const recent = readJson(RECENT_KEY, {});
  recent[String(id || '')] = Date.now();
  const trimmed = Object.fromEntries(Object.entries(recent).sort((left, right) => right[1] - left[1]).slice(0, 500));
  writeJson(RECENT_KEY, trimmed);
}

export function loadScenarioUsage() { return readJson(RECENT_KEY, {}); }

export function loadRehearsalHistory({ strict = false } = {}) {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]').map(normalizeRehearsalRecord).sort((left, right) => right.endedAt - left.endedAt); }
  catch {
    if (strict) throw new Error('Saved rehearsal history could not be read. It has not been overwritten. Allow site storage, retry loading, or restore a known backup before changing history.');
    return [];
  }
}

export function saveRehearsalRecord(room, notes = {}) {
  const record = createRehearsalRecord(room, notes);
  const history = loadRehearsalHistory({ strict: true });
  const existing = history.find((item) => item.id === record.id);
  if (existing) record.notes = existing.notes;
  const current = history.filter((item) => item.id !== record.id);
  localStorage.setItem(HISTORY_KEY, JSON.stringify([record, ...current].slice(0, 1000)));
  return record;
}

export function updateRehearsalNotes(id, notes) {
  const current = loadRehearsalHistory({ strict: true });
  if (!current.some(record => record.id === id)) throw new Error('That rehearsal record is no longer saved. Keep your note draft and retry loading history.');
  const history = current.map((record) => record.id === id ? { ...record, notes: { general: String(notes?.general || '').slice(0, 5000), moments: (notes?.moments || []).slice(0, 100) } } : record);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  return history.find((record) => record.id === id);
}

export function deleteRehearsalRecord(id) {
  const history = loadRehearsalHistory({ strict: true }).filter((record) => record.id !== id);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  return history;
}

export function clearRehearsalHistory() { localStorage.setItem(HISTORY_KEY, '[]'); }

function readJson(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); } catch { return fallback; }
}

function writeJson(key, value) { localStorage.setItem(key, JSON.stringify(value)); }

function loadPracticeProgress() {
  const entries = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key?.startsWith(PRACTICE_PREFIX)) continue;
    const snapshot = readJson(key, null);
    if (snapshot) entries.push({ scenarioId: key.slice(PRACTICE_PREFIX.length), snapshot });
  }
  return entries;
}

export function exportFullBackup() {
  const envelope = createAppBackup({
    scenarios: loadUserScenarios({ strict: true }),
    history: loadRehearsalHistory({ strict: true }),
    settings: readJson(SETTINGS_KEY, {}),
    favorites: readJson(FAVORITES_KEY, []),
    recentScenarios: readJson(RECENT_KEY, {}),
    practiceProgress: loadPracticeProgress(),
  });
  const date = new Date().toISOString().slice(0, 10);
  return downloadDataBackup(envelope, { filename: `QLine-The-Game-Backup-${date}.json` }, QLINE_BACKUP_POLICY);
}

export function exportScenario(scenario) {
  const envelope = createAppBackup({ scenarios: [normalizeScenario(scenario)] }, { kind: 'scenario' });
  const safeTitle = String(scenario.title || 'Scenario').replace(/[^a-z0-9._-]+/gi, '-').slice(0, 70);
  return downloadDataBackup(envelope, { filename: `QLine-The-Game-${safeTitle}.json` }, QLINE_BACKUP_POLICY);
}

export function previewBackup(text) { return inspectAppBackup(text); }

function refuseImportConflicts(existing, incoming, label) {
  const known = new Map(existing.map(item => [item.id, JSON.stringify(item)]));
  for (const item of incoming) {
    const content = JSON.stringify(item);
    if (known.has(item.id) && known.get(item.id) !== content) throw Object.assign(new Error(`This import has a different ${label} with an ID already saved here. Your local data has not changed. Export the current version and review the differences before using Settings to restore or replace it.`), { code: 'IMPORT_CONFLICT' });
    known.set(item.id, content);
  }
}

export function restoreBackup(text, mode = 'merge', { preserveExisting = false } = {}) {
  const inspected = inspectAppBackup(text);
  const payload = inspected.payload;
  const replace = mode === 'replace';
  const existingScenarios = replace ? [] : loadUserScenarios({ strict: true });
  const existingHistory = payload.kind === 'full' && !replace ? loadRehearsalHistory({ strict: true }) : [];
  if (preserveExisting && !replace) {
    refuseImportConflicts(existingScenarios, payload.scenarios, 'script');
    if (payload.kind === 'full') refuseImportConflicts(existingHistory, payload.history, 'rehearsal record');
  }
  const scenarios = replace ? payload.scenarios : mergeById(existingScenarios, payload.scenarios);
  const history = payload.kind === 'full' ? (replace ? payload.history : mergeById(existingHistory, payload.history)) : null;
  const existingPracticeKeys = payload.kind === 'full' ? Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).filter((key) => key?.startsWith(PRACTICE_PREFIX)) : [];
  const mutations = [];
  function changeItem(key, value, remove = false) {
    const previous = localStorage.getItem(key);
    if (remove) localStorage.removeItem(key);
    else writeJson(key, value);
    // Web Storage mutations are atomic. Journal only completed operations,
    // including separate removal and rewriting of the same practice key.
    mutations.push([key, previous]);
  }
  try {
    changeItem(KEY, scenarios);
    if (payload.kind === 'full') {
      changeItem(HISTORY_KEY, history);
      if (replace) existingPracticeKeys.forEach((key) => changeItem(key, null, true));
      if (replace || Object.keys(payload.settings || {}).length) changeItem(SETTINGS_KEY, replace ? payload.settings : { ...readJson(SETTINGS_KEY, {}), ...payload.settings });
      if (replace || payload.favorites?.length) changeItem(FAVORITES_KEY, replace ? payload.favorites : [...new Set([...readJson(FAVORITES_KEY, []), ...payload.favorites])]);
      if (replace || Object.keys(payload.recentScenarios || {}).length) changeItem(RECENT_KEY, replace ? payload.recentScenarios : { ...readJson(RECENT_KEY, {}), ...payload.recentScenarios });
      for (const entry of payload.practiceProgress || []) {
        if (!entry?.scenarioId || !entry.snapshot) continue;
        changeItem(`${PRACTICE_PREFIX}${String(entry.scenarioId).slice(0, 120)}`, entry.snapshot);
      }
    }
  } catch (error) {
    let restored = true;
    for (const [key, value] of mutations.reverse()) {
      try { if (value == null) localStorage.removeItem(key); else localStorage.setItem(key, value); }
      catch { restored = false; }
    }
    if (!restored) throw new Error('Restore failed and some earlier local data could not be restored. Keep this page open, free local storage or allow site storage, then restore a known backup.');
    throw new Error('Restore could not be completed. Your earlier local data was put back.');
  }
  return { kind: payload.kind, scenarios: payload.scenarios.length, history: payload.history?.length || 0, mode };
}
